/**
 * Upload Queue State Management
 * Handles bulk file uploads with individual progress tracking
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks for large files

export const uploadFileWithProgress = createAsyncThunk(
  'uploadQueue/uploadFile',
  async ({ file, folderId, uploadId }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (folderId) formData.append('folder_id', folderId);

      const response = await api.post('/documents/upload', formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch(updateUploadProgress({ uploadId, progress }));
        },
      });

      return { uploadId, document: response.data };
    } catch (error) {
      return rejectWithValue({
        uploadId,
        error: error.response?.data?.detail || 'Upload failed',
      });
    }
  }
);

export const initiateChunkedUpload = createAsyncThunk(
  'uploadQueue/initiateChunked',
  async ({ file, folderId, uploadId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/documents/upload/initiate', {
        filename: file.name,
        file_size: file.size,
        file_type: file.name.split('.').pop().toLowerCase(),
        folder_id: folderId,
        chunk_size: CHUNK_SIZE,
      });
      return { uploadId, session: response.data };
    } catch (error) {
      return rejectWithValue({
        uploadId,
        error: error.response?.data?.detail || 'Failed to initiate upload',
      });
    }
  }
);

export const uploadChunk = createAsyncThunk(
  'uploadQueue/uploadChunk',
  async ({ file, sessionId, chunkIndex, uploadId, totalChunks }, { dispatch, rejectWithValue }) => {
    try {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('session_id', sessionId);
      formData.append('chunk_index', chunkIndex);

      await api.post('/documents/upload/chunk', formData);

      const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
      dispatch(updateUploadProgress({ uploadId, progress }));

      return { uploadId, chunkIndex, completed: chunkIndex === totalChunks - 1 };
    } catch (error) {
      return rejectWithValue({
        uploadId,
        chunkIndex,
        error: error.response?.data?.detail || 'Chunk upload failed',
      });
    }
  }
);

export const completeChunkedUpload = createAsyncThunk(
  'uploadQueue/completeChunked',
  async ({ sessionId, uploadId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/documents/upload/complete', {
        session_id: sessionId,
      });
      return { uploadId, document: response.data };
    } catch (error) {
      return rejectWithValue({
        uploadId,
        error: error.response?.data?.detail || 'Failed to complete upload',
      });
    }
  }
);

export const resumeUpload = createAsyncThunk(
  'uploadQueue/resume',
  async ({ sessionId, uploadId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/documents/upload/status/${sessionId}`);
      return { uploadId, status: response.data };
    } catch (error) {
      return rejectWithValue({
        uploadId,
        error: error.response?.data?.detail || 'Failed to get upload status',
      });
    }
  }
);

const uploadQueueSlice = createSlice({
  name: 'uploadQueue',
  initialState: {
    queue: [],
    activeUploads: 0,
    maxConcurrent: 3,
    isProcessing: false,
    completedCount: 0,
    failedCount: 0,
    sessions: {},
  },
  reducers: {
    addToQueue: (state, action) => {
      const { files, folderId } = action.payload;
      const newItems = files.map((file, index) => ({
        id: `upload_${Date.now()}_${index}`,
        file: {
          name: file.name,
          size: file.size,
          type: file.type,
        },
        fileObject: file,
        folderId,
        status: 'queued',
        progress: 0,
        error: null,
        documentId: null,
        sessionId: null,
        uploadedChunks: 0,
        totalChunks: Math.ceil(file.size / CHUNK_SIZE),
        isChunked: file.size > CHUNK_SIZE,
        addedAt: Date.now(),
      }));
      state.queue.push(...newItems);
    },
    removeFromQueue: (state, action) => {
      const uploadId = action.payload;
      state.queue = state.queue.filter((item) => item.id !== uploadId);
    },
    clearQueue: (state) => {
      state.queue = state.queue.filter(
        (item) => item.status === 'uploading' || item.status === 'processing'
      );
    },
    clearCompleted: (state) => {
      state.queue = state.queue.filter(
        (item) => item.status !== 'completed' && item.status !== 'failed'
      );
      state.completedCount = 0;
      state.failedCount = 0;
    },
    updateUploadProgress: (state, action) => {
      const { uploadId, progress } = action.payload;
      const item = state.queue.find((i) => i.id === uploadId);
      if (item) {
        item.progress = progress;
      }
    },
    setUploadStatus: (state, action) => {
      const { uploadId, status, error, documentId, sessionId } = action.payload;
      const item = state.queue.find((i) => i.id === uploadId);
      if (item) {
        item.status = status;
        if (error !== undefined) item.error = error;
        if (documentId !== undefined) item.documentId = documentId;
        if (sessionId !== undefined) item.sessionId = sessionId;
      }
    },
    incrementActiveUploads: (state) => {
      state.activeUploads += 1;
    },
    decrementActiveUploads: (state) => {
      state.activeUploads = Math.max(0, state.activeUploads - 1);
    },
    setProcessing: (state, action) => {
      state.isProcessing = action.payload;
    },
    retryUpload: (state, action) => {
      const uploadId = action.payload;
      const item = state.queue.find((i) => i.id === uploadId);
      if (item) {
        item.status = 'queued';
        item.progress = 0;
        item.error = null;
        if (item.failedCount) {
          state.failedCount -= 1;
        }
      }
    },
    saveSession: (state, action) => {
      const { uploadId, sessionId, filename, uploadedChunks, totalChunks } = action.payload;
      state.sessions[sessionId] = {
        uploadId,
        filename,
        uploadedChunks,
        totalChunks,
        savedAt: Date.now(),
      };
      localStorage.setItem('uploadSessions', JSON.stringify(state.sessions));
    },
    loadSessions: (state) => {
      try {
        const saved = localStorage.getItem('uploadSessions');
        if (saved) {
          state.sessions = JSON.parse(saved);
        }
      } catch (e) {
        state.sessions = {};
      }
    },
    removeSession: (state, action) => {
      const sessionId = action.payload;
      delete state.sessions[sessionId];
      localStorage.setItem('uploadSessions', JSON.stringify(state.sessions));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadFileWithProgress.pending, (state, action) => {
        const uploadId = action.meta.arg.uploadId;
        const item = state.queue.find((i) => i.id === uploadId);
        if (item) {
          item.status = 'uploading';
        }
      })
      .addCase(uploadFileWithProgress.fulfilled, (state, action) => {
        const { uploadId, document } = action.payload;
        const item = state.queue.find((i) => i.id === uploadId);
        if (item) {
          item.status = 'completed';
          item.progress = 100;
          item.documentId = document.id;
          state.completedCount += 1;
        }
        state.activeUploads = Math.max(0, state.activeUploads - 1);
      })
      .addCase(uploadFileWithProgress.rejected, (state, action) => {
        const { uploadId, error } = action.payload || {};
        if (uploadId) {
          const item = state.queue.find((i) => i.id === uploadId);
          if (item) {
            item.status = 'failed';
            item.error = error || 'Upload failed';
            state.failedCount += 1;
          }
        }
        state.activeUploads = Math.max(0, state.activeUploads - 1);
      })
      .addCase(initiateChunkedUpload.pending, (state, action) => {
        const uploadId = action.meta.arg.uploadId;
        const item = state.queue.find((i) => i.id === uploadId);
        if (item) {
          item.status = 'uploading';
        }
      })
      .addCase(initiateChunkedUpload.fulfilled, (state, action) => {
        const { uploadId, session } = action.payload;
        const item = state.queue.find((i) => i.id === uploadId);
        if (item) {
          item.sessionId = session.session_id;
          item.status = 'uploading';
        }
      })
      .addCase(initiateChunkedUpload.rejected, (state, action) => {
        const { uploadId, error } = action.payload || {};
        if (uploadId) {
          const item = state.queue.find((i) => i.id === uploadId);
          if (item) {
            item.status = 'failed';
            item.error = error || 'Failed to initiate upload';
            state.failedCount += 1;
          }
        }
        state.activeUploads = Math.max(0, state.activeUploads - 1);
      })
      .addCase(uploadChunk.fulfilled, (state, action) => {
        const { uploadId, chunkIndex } = action.payload;
        const item = state.queue.find((i) => i.id === uploadId);
        if (item) {
          item.uploadedChunks = chunkIndex + 1;
        }
      })
      .addCase(uploadChunk.rejected, (state, action) => {
        const { uploadId, error } = action.payload || {};
        if (uploadId) {
          const item = state.queue.find((i) => i.id === uploadId);
          if (item) {
            item.status = 'paused';
            item.error = error || 'Chunk upload failed - can resume';
          }
        }
      })
      .addCase(completeChunkedUpload.fulfilled, (state, action) => {
        const { uploadId, document } = action.payload;
        const item = state.queue.find((i) => i.id === uploadId);
        if (item) {
          item.status = 'completed';
          item.progress = 100;
          item.documentId = document.id;
          state.completedCount += 1;
          if (item.sessionId) {
            delete state.sessions[item.sessionId];
            localStorage.setItem('uploadSessions', JSON.stringify(state.sessions));
          }
        }
        state.activeUploads = Math.max(0, state.activeUploads - 1);
      })
      .addCase(completeChunkedUpload.rejected, (state, action) => {
        const { uploadId, error } = action.payload || {};
        if (uploadId) {
          const item = state.queue.find((i) => i.id === uploadId);
          if (item) {
            item.status = 'failed';
            item.error = error || 'Failed to complete upload';
            state.failedCount += 1;
          }
        }
        state.activeUploads = Math.max(0, state.activeUploads - 1);
      });
  },
});

export const {
  addToQueue,
  removeFromQueue,
  clearQueue,
  clearCompleted,
  updateUploadProgress,
  setUploadStatus,
  incrementActiveUploads,
  decrementActiveUploads,
  setProcessing,
  retryUpload,
  saveSession,
  loadSessions,
  removeSession,
} = uploadQueueSlice.actions;

export default uploadQueueSlice.reducer;
